import {
  TextField,
  Grid,
  Button,
  FormHelperText,
  Typography,
  Box,
  IconButton,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio
} from '@mui/material';

import {Formik, Form, FormikHelpers} from 'formik';

import {useCallback, useEffect, useState} from 'react';

import {useTranslation} from 'react-i18next';

import {useDispatch} from 'react-redux';

import {useAppSelector} from '@/redux-store/store';

import {AddressDetails} from '@/types/billing-data';
import TypographyTitle from '@/components/ui/typography-title';
import LocationAutocomplete from '@/components/forms/input/location/location-autocomplete';
import {
  resetOdooCompanyData,
  setOdooCompanyData
} from '@/redux-store/slices/odoo-company';
import OdooCompanyValidationSchema, {
  EMAIL_MAX_LENGTH,
  NAME_MAX_LENGTH
} from '@/schemas/odoo-company';
import CachedIcon from '@mui/icons-material/Cached';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import {createOdooCompany} from '@/rest/payments/create-odoo-company';
import {setUserInfos} from '@/redux-store/slices/user-infos';
import LayoutLoading from '@/components/layout/layout-loading';
import FormLabel from '@/components/ui/form-label';
import {OdooCompany} from '@/types/payments';
import {getCompanyTax} from '@/rest/payments/get-company-tax';

interface OdooCompanyFormProps {
  onCancel?: () => void;
  onSubmit?: () => void;
  onError?: (error: Error) => void;
}

const OdooCompanyForm: React.FC<OdooCompanyFormProps> = ({
  onCancel,
  onSubmit,
  onError
}) => {
  const {t} = useTranslation();

  const companyValidationSchema = OdooCompanyValidationSchema(t);

  const [fileSizeError, setFileSizeError] = useState<string | null>(null);
  const [formSubmitted, setFormSubmitted] = useState(false);

  const initialValues = useAppSelector(
    (state) => state.odooCompany.odooCompany
  );
  const userInfos = useAppSelector((state) => state.userInfos.userInfos);

  const dispatch = useDispatch();

  const handleImageChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setFieldValue: FormikHelpers<any>['setFieldValue']
  ) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      const fileSize = file.size / 1024 / 1024;
      if (fileSize > 1) {
        setFileSizeError(t('file_size_error', {maxSize: 1000, unit: 'KB'}));

        return;
      }
      const reader = new FileReader();
      reader.readAsDataURL(event.target.files[0]);
      reader.onload = () => {
        dispatch(
          setOdooCompanyData({
            logo: reader.result as string
          })
        );
        setFieldValue('logo', reader.result as string);
        setFileSizeError(null);
      };
    }
  };

  const handleSubmitForm = useCallback(async () => {
    try {
      setFormSubmitted(true);

      const response = await createOdooCompany(initialValues);

      if (!response) {
        throw new Error('Error creating odoo company');
      }

      dispatch(
        setUserInfos({
          odooCompany: response.company
        })
      );

      await getCompanyTax(response.company);

      if (onSubmit) {
        onSubmit();
      }
    } catch (error) {
      if (onError) {
        onError(error as Error);
      }
    } finally {
      setFormSubmitted(false);
    }
  }, [dispatch, initialValues, onError, onSubmit]);

  const handleDeleteImage = useCallback(() => {
    dispatch(
      setOdooCompanyData({
        logo: null
      })
    );
    setFileSizeError(null);
  }, [dispatch]);

  const resetFormData = useCallback(() => {
    setFileSizeError(null);
    dispatch(resetOdooCompanyData());
  }, [dispatch]);

  const computedInitialState = {
    ...initialValues,
    name: initialValues.name || userInfos.name,
    email: initialValues.email || userInfos.email
  };

  useEffect(() => {
    if (userInfos) {
      const updates: Partial<OdooCompany> = {};
      if (!initialValues.name && userInfos.name) {
        updates.name = userInfos.name;
      }
      if (!initialValues.email && userInfos.email) {
        updates.email = userInfos.email;
      }

      if (Object.keys(updates).length > 0) {
        dispatch(setOdooCompanyData(updates));
      }
    }
  }, [dispatch, initialValues.email, initialValues.name, userInfos]);

  if (formSubmitted) {
    return <LayoutLoading message="creating_company" />;
  }

  return (
    <>
      <Formik
        initialValues={computedInitialState}
        validationSchema={companyValidationSchema}
        validateOnBlur={true}
        validateOnChange={true}
        onSubmit={(values) => {
          dispatch(setOdooCompanyData(values));
          handleSubmitForm();
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          setFieldValue
        }) => {
          // console.log('errors', errors);

          // console.log('values: ', values);

          return (
            <Form>
              <TypographyTitle textKey={t('invoicing_details')} />
              <Grid container spacing={2} my={1}>
                <Grid item xs={12}>
                  <TextField
                    name="name"
                    label={t('prop_user_name')}
                    value={values.name}
                    onChange={(e) => {
                      handleChange(e);
                      dispatch(
                        setOdooCompanyData({
                          name: e.target.value
                        })
                      );
                    }}
                    onBlur={handleBlur}
                    error={touched.name && Boolean(errors.name)}
                    helperText={touched.name && errors.name}
                    fullWidth
                  />
                  <FormHelperText>
                    {values.name.length}/ {NAME_MAX_LENGTH}
                  </FormHelperText>
                </Grid>
              </Grid>

              <Grid container spacing={2} my={1}>
                <Grid item xs={12}>
                  <LocationAutocomplete
                    initialAddress={initialValues.address?.fullAddress || ''}
                    onAddressSelected={(address: AddressDetails) => {
                      setFieldValue('address', address);
                      dispatch(
                        setOdooCompanyData({
                          address: address
                        })
                      );
                    }}
                    error={
                      touched.address && errors.address
                        ? errors.address
                        : undefined
                    }
                    touched={!!touched['address']}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2} mt={3}>
                <Grid item xs={12} sm={12}>
                  <TextField
                    name="email"
                    label={t('email')}
                    value={values.email}
                    onChange={(e) => {
                      handleChange(e);
                      dispatch(
                        setOdooCompanyData({
                          email: e.target.value
                        })
                      );
                    }}
                    onBlur={handleBlur}
                    error={touched.email && Boolean(errors.email)}
                    helperText={touched.email && errors.email}
                    fullWidth
                  />
                  <FormHelperText>
                    {values.name.length}/ {EMAIL_MAX_LENGTH}
                  </FormHelperText>
                </Grid>
              </Grid>

              <Grid container spacing={2} mt={3}>
                <Grid item xs={12} sm={12}>
                  <TextField
                    name="website"
                    label={<FormLabel text="website" />}
                    value={values.website}
                    onChange={(e) => {
                      handleChange(e);
                      dispatch(
                        setOdooCompanyData({
                          website: e.target.value
                        })
                      );
                    }}
                    onBlur={handleBlur}
                    error={touched.website && Boolean(errors.website)}
                    helperText={touched.website && errors.website}
                    fullWidth
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2} mt={3}>
                <Grid item xs={12} sm={12}>
                  <TextField
                    name="phoneNumber"
                    label={<FormLabel text="phoneNumber" />}
                    value={values.phoneNumber}
                    onChange={(e) => {
                      handleChange(e);
                      dispatch(
                        setOdooCompanyData({
                          phoneNumber: e.target.value
                        })
                      );
                    }}
                    onBlur={handleBlur}
                    error={touched.phoneNumber && Boolean(errors.phoneNumber)}
                    helperText={touched.phoneNumber && errors.phoneNumber}
                    fullWidth
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2} mt={3} mb={2}>
                <Grid
                  item
                  xs={12}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}
                >
                  <FormControl component="fieldset">
                    <FormLabel text={t('use19vat')} required />
                    <RadioGroup
                      row
                      value={values.use19vat}
                      onChange={(e) => {
                        setFieldValue('use19vat', e.target.value === 'true');
                        dispatch(
                          setOdooCompanyData({
                            use19vat: e.target.value === 'true'
                          })
                        );
                      }}
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                      }}
                    >
                      <FormControlLabel
                        value="false"
                        control={<Radio />}
                        label={t('No')}
                      />
                      <FormControlLabel
                        value="true"
                        control={<Radio />}
                        label={t('Yes')}
                      />
                    </RadioGroup>
                    {touched.use19vat && Boolean(errors.use19vat) && (
                      <FormHelperText error>{errors.use19vat}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
              </Grid>

              <Grid
                container
                spacing={2}
                marginBottom={3}
                marginTop={{xs: 0, sm: 3}}
                justifyContent={'center'}
              >
                <Grid
                  item
                  xs={12}
                  sm={6}
                  sx={{display: 'flex', justifyContent: 'center'}}
                >
                  <Button
                    component="span"
                    variant="outlined"
                    color="error"
                    startIcon={<CachedIcon />}
                    onClick={handleDeleteImage}
                    sx={{
                      display: {xs: 'none', sm: 'flex'},
                      textTransform: 'none'
                    }}
                  >
                    {t('delete_logo')}
                  </Button>
                </Grid>

                <Grid
                  item
                  xs={12}
                  sm={6}
                  sx={{display: 'flex', justifyContent: 'center'}}
                >
                  <label htmlFor="photo-upload">
                    <Button
                      component="span"
                      variant="outlined"
                      color="secondary"
                      startIcon={<PhotoCamera />}
                      sx={{
                        display: {xs: 'none', sm: 'flex'},
                        textTransform: 'none'
                      }}
                    >
                      {t('upload_logo')}
                    </Button>
                  </label>
                </Grid>

                <Grid
                  item
                  xs={12}
                  sm={6}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginRight: 2,
                    marginBottom: 1
                  }}
                >
                  <label htmlFor="phote-delete-xs">
                    <IconButton
                      aria-label={t('delete')}
                      color="error"
                      onClick={handleDeleteImage}
                      component="span"
                      sx={{
                        display: {xs: 'flex', sm: 'none'}
                      }}
                    >
                      <CachedIcon />
                    </IconButton>
                  </label>

                  <input
                    id="photo-upload"
                    type="file"
                    onChange={(event) =>
                      handleImageChange(event, setFieldValue)
                    }
                    accept=".jpg, .jpeg, .png"
                    hidden
                  />

                  <label htmlFor="photo-upload-xs">
                    <IconButton
                      color="secondary"
                      aria-label={t('upload_photo')}
                      component="span"
                      sx={{
                        display: {xs: 'flex', sm: 'none'},
                        marginLeft: 4,
                        marginTop: '1px'
                      }}
                    >
                      <PhotoCamera />
                    </IconButton>
                  </label>
                  <input
                    id="photo-upload-xs"
                    type="file"
                    onChange={(event) =>
                      handleImageChange(event, setFieldValue)
                    }
                    accept=".jpg, .jpeg, .png"
                    hidden
                  />
                </Grid>
              </Grid>

              <Grid
                item
                xs={12}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flexDirection: 'column',
                  marginTop: {xs: 0, sm: 1}
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: 2,
                    flexDirection: 'column'
                  }}
                >
                  <Typography variant="subtitle1" align="center">
                    {t('company_logo')}
                  </Typography>
                  <Typography variant="subtitle1" align="center">
                    {t('optimal_dimensions', {width: 3, height: 1})}
                  </Typography>
                </Box>
                {initialValues.logo && (
                  <Box
                    component="img"
                    src={initialValues.logo as string}
                    alt={t('company_logo')}
                    sx={{
                      objectFit: 'cover',
                      width: {xs: '240px', sm: '300px'},
                      height: {xs: '80px', sm: '100px'},
                      marginTop: {xs: 2, sm: 0}
                    }}
                  />
                )}
              </Grid>

              {fileSizeError && (
                <Typography align="center" color="error">
                  {fileSizeError}
                </Typography>
              )}

              <Grid
                container
                direction={{xs: 'column-reverse', sm: 'row'}}
                spacing={2}
                sx={{
                  my: {xs: 1, sm: 3}
                }}
              >
                <Grid
                  item
                  xs={12}
                  sm={6}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginBottom: {xs: 2, sm: 0},
                    marginTop: {xs: 1, sm: 0}
                  }}
                >
                  <Button
                    variant="contained"
                    disabled={formSubmitted}
                    color="primary"
                    onClick={async () => {
                      resetFormData();

                      if (onCancel) {
                        onCancel();
                      }
                    }}
                    sx={{
                      width: {xs: '50%', sm: '80%'}
                    }}
                  >
                    {t('cancel')}
                  </Button>
                </Grid>

                <Grid
                  item
                  xs={12}
                  sm={6}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginBottom: {xs: 2, sm: 0},
                    marginTop: {xs: 1, sm: 0}
                  }}
                >
                  <Button
                    variant="contained"
                    disabled={formSubmitted}
                    color="primary"
                    onClick={() => {
                      handleSubmit();
                    }}
                    sx={{
                      width: {xs: '50%', sm: '80%'}
                    }}
                  >
                    {t('continue')}
                  </Button>
                </Grid>
              </Grid>
            </Form>
          );
        }}
      </Formik>
    </>
  );
};

export default OdooCompanyForm;
